/*
  Midnight Longhouse: storefront behaviour.
  Depends on ml-core.js (pure helpers) and on Dawn's globals (routes, fetchConfig, subscribe/publish).
  Custom elements: free-shipping bar, bundle builder, sticky add-to-cart, size guide, cart upsell,
  discount-code field, recently viewed.
*/
(function () {
  'use strict';

  var Core = window.MLCore;
  var cfg = window.mlConfig || {};
  var strings = window.mlStrings || {};

  function t(key, values) {
    var s = strings[key];
    if (typeof s !== 'string') return '';
    return values ? Core.substitute(s, values) : s;
  }

  function activeCurrency() {
    var s = window.Shopify;
    return (s && s.currency && s.currency.active) || cfg.shopCurrency || '';
  }

  function currencyRate() {
    var s = window.Shopify;
    var r = s && s.currency ? Number(s.currency.rate) : 1;
    return isFinite(r) && r > 0 ? r : 1;
  }

  function money(cents) {
    var active = activeCurrency();
    if (active && cfg.shopCurrency && active !== cfg.shopCurrency && window.Intl && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: active,
        }).format(cents / 100);
      } catch (e) {
        /* fall through to the shop money format */
      }
    }
    return Core.formatMoney(cents, cfg.moneyFormat);
  }

  function getCartComponent() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function addItemsToCart(items) {
    var cart = getCartComponent();
    var body = { items: items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
      body.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }
    return fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || response.message || t('cartError'));
        }
        if (!cart) {
          window.location = window.routes.cart_url;
          return response;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ml-theme', cartData: response });
        }
        cart.renderContents(response);
        return response;
      });
  }

  /* ---------- Free shipping progress ---------- */
  if (!customElements.get('ml-free-shipping')) {
    customElements.define(
      'ml-free-shipping',
      class MLFreeShipping extends HTMLElement {
        connectedCallback() {
          // Server-side Liquid already rendered the state when the cart currency equals the shop currency.
          // When it differs (Shopify Markets), recalculate here using Shopify.currency.rate.
          if (this.dataset.needsRate !== 'true') return;
          var state = Core.freeShippingState(
            Number(this.dataset.total),
            Number(this.dataset.threshold),
            currencyRate()
          );
          if (!state.enabled) return;
          var text = this.querySelector('[data-ml-fs-text]');
          var bar = this.querySelector('[data-ml-fs-bar]');
          var fill = this.querySelector('[data-ml-fs-fill]');
          if (text) {
            text.textContent = state.reached
              ? t('freeShippingReached')
              : Core.substitute(this.dataset.progressTemplate || t('freeShippingProgress'), { amount: money(state.remaining) });
          }
          if (fill) fill.style.width = state.percent + '%';
          if (bar) bar.setAttribute('aria-valuenow', String(state.percent));
          this.classList.toggle('is-reached', state.reached);
          this.removeAttribute('hidden');
        }
      }
    );
  }

  /* ---------- Bundle builder ---------- */
  if (!customElements.get('bundle-builder')) {
    customElements.define(
      'bundle-builder',
      class BundleBuilder extends HTMLElement {
        connectedCallback() {
          this.items = Array.prototype.slice.call(this.querySelectorAll('[data-bundle-item]'));
          this.addButton = this.querySelector('[data-bundle-add]');
          this.errorEl = this.querySelector('[data-bundle-error]');
          this.percent = Number(this.dataset.percent) || 0;
          this.minItems = Number(this.dataset.minItems) || 2;
          this.showSavings = this.dataset.showSavings === 'true';
          this.savingsLabel = this.dataset.savingsLabel || '';

          var self = this;
          this.items.forEach(function (item) {
            var check = item.querySelector('[data-bundle-check]');
            var select = item.querySelector('[data-bundle-variant]');
            if (check) check.addEventListener('change', function () { self.update(); });
            if (select) select.addEventListener('change', function () { self.update(); });
          });
          if (this.addButton) this.addButton.addEventListener('click', function () { self.addAll(); });

          // Keep the current-product row in step with the variant picked in the main product form.
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
              var variant = event && event.data && event.data.variant;
              if (!variant) return;
              var row = self.querySelector('[data-bundle-item][data-current="true"]');
              var select = row && row.querySelector('[data-bundle-variant]');
              if (!select) return;
              var opt = select.querySelector('option[value="' + variant.id + '"]');
              if (opt) {
                select.value = String(variant.id);
                self.update();
              }
            });
          }
          this.update();
        }

        disconnectedCallback() {
          if (this.unsubscribe) this.unsubscribe();
        }

        selectedOption(item) {
          var select = item.querySelector('[data-bundle-variant]');
          if (select) return select.options[select.selectedIndex];
          return item.querySelector('[data-bundle-single]');
        }

        update() {
          var chosen = [];
          this.items.forEach(function (item) {
            var check = item.querySelector('[data-bundle-check]');
            var opt = this.selectedOption(item);
            var priceEl = item.querySelector('[data-bundle-price]');
            var available = opt && opt.dataset.available === 'true';
            if (opt && priceEl) {
              var price = Number(opt.dataset.price) || 0;
              var cmp = Number(opt.dataset.compare) || 0;
              priceEl.innerHTML = '';
              if (cmp > price) {
                var s = document.createElement('s');
                s.className = 'ml-bundle__was';
                s.textContent = money(cmp);
                priceEl.appendChild(s);
                priceEl.appendChild(document.createTextNode(' '));
              }
              priceEl.appendChild(document.createTextNode(money(price)));
            }
            if (check && !available) {
              check.checked = false;
              check.disabled = true;
            } else if (check && check.dataset.locked !== 'true') {
              check.disabled = false;
            }
            item.classList.toggle('is-selected', !!(check && check.checked));
            item.classList.toggle('is-soldout', !available);
            if (check && check.checked && available) {
              chosen.push({
                id: opt.value,
                price: Number(opt.dataset.price) || 0,
                compare_at: Number(opt.dataset.compare) || 0,
              });
            }
          }, this);

          this.chosen = chosen;
          var totals = Core.bundleTotals(chosen, {
            percent: this.showSavings ? this.percent : 0,
            minItems: this.minItems,
          });
          this.totals = totals;

          var set = function (sel, text) {
            var el = this.querySelector(sel);
            if (el) el.textContent = text;
          }.bind(this);

          set('[data-bundle-count]', totals.count === 1 ? t('bundleCountOne') : t('bundleCountOther', { count: totals.count }));
          set('[data-bundle-total]', money(totals.total));

          var orig = this.querySelector('[data-bundle-original]');
          if (orig) {
            orig.hidden = !(totals.saleSavings > 0);
            orig.textContent = money(totals.original);
          }
          var sale = this.querySelector('[data-bundle-sale-savings]');
          if (sale) {
            sale.hidden = !(totals.saleSavings > 0);
            sale.textContent = t('bundleSaleSavings', { amount: money(totals.saleSavings) });
          }
          var disc = this.querySelector('[data-bundle-discount]');
          if (disc) {
            var show = this.showSavings && totals.bundleDiscount > 0;
            disc.hidden = !show;
            if (show) {
              disc.textContent = Core.substitute(this.savingsLabel || t('bundleDiscountDefault'), {
                percent: this.percent + '%',
                amount: money(totals.bundleDiscount),
              });
            }
          }
          if (this.addButton) {
            this.addButton.disabled = totals.count === 0;
            this.addButton.textContent = totals.count === 0 ? t('bundleChoose') : t('bundleAddAll');
          }
        }

        addAll() {
          if (!this.chosen || !this.chosen.length) return;
          var items = Core.buildAddItems(this.chosen.map(function (c) { return c.id; }));
          var btn = this.addButton;
          var self = this;
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('loading');
          if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
          addItemsToCart(items)
            .catch(function (e) {
              if (self.errorEl) {
                self.errorEl.textContent = e && e.message ? e.message : t('cartError');
                self.errorEl.hidden = false;
              }
            })
            .then(function () {
              btn.removeAttribute('aria-disabled');
              btn.classList.remove('loading');
            });
        }
      }
    );
  }

  /* ---------- Sticky add-to-cart (mobile) ---------- */
  if (!customElements.get('sticky-atc')) {
    customElements.define(
      'sticky-atc',
      class StickyATC extends HTMLElement {
        connectedCallback() {
          var section = this.dataset.section;
          this.mainButton = document.getElementById('ProductSubmitButton-' + section);
          this.button = this.querySelector('[data-sticky-button]');
          this.priceEl = this.querySelector('[data-sticky-price]');
          if (!this.mainButton || !this.button) return;

          var self = this;
          this.button.addEventListener('click', function () {
            if (self.mainButton.disabled) return;
            self.mainButton.click();
          });

          if ('IntersectionObserver' in window) {
            this.io = new IntersectionObserver(function (entries) {
              var e = entries[entries.length - 1];
              self.toggle(Core.shouldShowSticky(e.boundingClientRect, window.innerHeight));
            });
            this.io.observe(this.mainButton);
          }

          // Mirror price and button state when the variant changes (Dawn swaps these nodes).
          var priceSource = document.getElementById('price-' + section);
          this.syncPrice = function () {
            var src = document.getElementById('price-' + section);
            if (!src || !self.priceEl) return;
            var sale = src.querySelector('.price-item--sale');
            var regular = src.querySelector('.price-item--regular');
            var onSale = src.querySelector('.price--on-sale');
            var node = onSale && sale ? sale : regular;
            self.priceEl.textContent = node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
          };
          this.syncButton = function () {
            var b = document.getElementById('ProductSubmitButton-' + section);
            if (!b) return;
            self.mainButton = b;
            self.button.disabled = b.hasAttribute('disabled');
            var label = b.querySelector('span');
            self.button.textContent = label ? label.textContent.trim() : self.button.textContent;
          };
          this.syncPrice();
          this.syncButton();
          if (priceSource && 'MutationObserver' in window) {
            this.mo = new MutationObserver(function () { self.syncPrice(); });
            this.mo.observe(priceSource, { childList: true, subtree: true, characterData: true });
          }
          if ('MutationObserver' in window) {
            this.mo2 = new MutationObserver(function () { self.syncButton(); });
            this.mo2.observe(this.mainButton, { attributes: true, childList: true, subtree: true, characterData: true });
          }
        }

        toggle(show) {
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', show ? 'false' : 'true');
          if (this.button) this.button.tabIndex = show ? 0 : -1;
        }

        disconnectedCallback() {
          if (this.io) this.io.disconnect();
          if (this.mo) this.mo.disconnect();
          if (this.mo2) this.mo2.disconnect();
        }
      }
    );
  }

  /* ---------- Size guide dialog ---------- */
  if (!customElements.get('ml-size-guide')) {
    customElements.define(
      'ml-size-guide',
      class MLSizeGuide extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
          this.opener = this.querySelector('[data-size-guide-open]');
          if (!this.dialog || !this.opener) return;
          var self = this;
          this.opener.addEventListener('click', function () {
            if (typeof self.dialog.showModal === 'function') {
              self.dialog.showModal();
              document.body.classList.add('overflow-hidden');
            } else {
              self.dialog.setAttribute('open', '');
            }
          });
          this.querySelectorAll('[data-size-guide-close]').forEach(function (b) {
            b.addEventListener('click', function () { self.close(); });
          });
          this.dialog.addEventListener('click', function (e) {
            if (e.target === self.dialog) self.close();
          });
          this.dialog.addEventListener('close', function () {
            document.body.classList.remove('overflow-hidden');
            self.opener.focus();
          });
        }

        close() {
          if (typeof this.dialog.close === 'function') this.dialog.close();
          else this.dialog.removeAttribute('open');
        }
      }
    );
  }

  /* ---------- Cart upsell (one-click add) ---------- */
  if (!customElements.get('cart-upsell')) {
    customElements.define(
      'cart-upsell',
      class CartUpsell extends HTMLElement {
        connectedCallback() {
          var self = this;
          this.button = this.querySelector('[data-upsell-add]');
          this.select = this.querySelector('[data-upsell-variant]');
          this.error = this.querySelector('[data-upsell-error]');
          if (!this.button) return;
          if (this.select) {
            this.select.addEventListener('change', function () { self.syncPrice(); });
            this.syncPrice();
          }
          this.button.addEventListener('click', function () {
            var id = self.select ? self.select.value : self.button.dataset.variantId;
            if (!id) return;
            self.button.setAttribute('aria-disabled', 'true');
            self.button.classList.add('loading');
            if (self.error) { self.error.hidden = true; self.error.textContent = ''; }
            addItemsToCart([{ id: Number(id), quantity: 1 }])
              .catch(function (e) {
                if (self.error) {
                  self.error.textContent = e && e.message ? e.message : t('cartError');
                  self.error.hidden = false;
                }
                self.button.removeAttribute('aria-disabled');
                self.button.classList.remove('loading');
              });
            // On success the drawer section re-renders, replacing this element.
          });
        }

        syncPrice() {
          var opt = this.select.options[this.select.selectedIndex];
          var priceEl = this.querySelector('[data-upsell-price]');
          if (opt && priceEl && opt.dataset.price) priceEl.textContent = money(Number(opt.dataset.price));
        }
      }
    );
  }

  /* ---------- Discount code hand-off to checkout ---------- */
  var DISCOUNT_KEY = 'ml_discount_code';
  function readCode() {
    try { return window.sessionStorage.getItem(DISCOUNT_KEY) || ''; } catch (e) { return ''; }
  }
  function writeCode(code) {
    try {
      if (code) window.sessionStorage.setItem(DISCOUNT_KEY, code);
      else window.sessionStorage.removeItem(DISCOUNT_KEY);
    } catch (e) { /* storage unavailable: the code is then only kept while the drawer stays rendered */ }
  }

  if (!customElements.get('ml-discount-code')) {
    customElements.define(
      'ml-discount-code',
      class MLDiscountCode extends HTMLElement {
        connectedCallback() {
          var self = this;
          this.input = this.querySelector('[data-discount-input]');
          this.apply = this.querySelector('[data-discount-apply]');
          this.status = this.querySelector('[data-discount-status]');
          this.clear = this.querySelector('[data-discount-clear]');
          if (!this.input || !this.apply) return;
          var saved = readCode();
          if (saved) this.input.value = saved;
          this.render(saved);
          this.apply.addEventListener('click', function () { self.save(); });
          this.input.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); self.save(); }
          });
          if (this.clear) {
            this.clear.addEventListener('click', function () {
              writeCode('');
              self.input.value = '';
              self.render('');
              self.input.focus();
            });
          }
        }

        save() {
          var code = this.input.value.trim();
          writeCode(code);
          this.render(code);
        }

        render(code) {
          if (this.status) {
            this.status.textContent = code ? t('discountSaved', { code: code }) : '';
            this.status.hidden = !code;
          }
          if (this.clear) this.clear.hidden = !code;
        }
      }
    );
  }

  // Delegated: works for the drawer and the /cart page, and survives section re-renders.
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest && event.target.closest('button[name="checkout"], input[name="checkout"]');
      if (!btn || btn.disabled) return;
      var typed = document.querySelector('ml-discount-code [data-discount-input]');
      var code = (typed && typed.value.trim()) || readCode();
      if (!code) return;
      event.preventDefault();
      writeCode(code);
      var checkoutUrl = cfg.checkoutUrl || '/checkout';
      var target = Core.buildDiscountUrl(cfg.discountHandoff, code, checkoutUrl);
      var noteEl = document.querySelector('textarea[name="note"]');
      var go = function () { window.location.href = target; };
      if (noteEl) {
        fetch(window.routes.cart_update_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: noteEl.value }),
        }).then(go, go);
      } else {
        go();
      }
    },
    true
  );

  /* ---------- Recently viewed ---------- */
  var RECENT_KEY = 'ml_recently_viewed';
  function readRecent() {
    try {
      var arr = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(arr) ? arr.filter(function (x) { return typeof x === 'string'; }) : [];
    } catch (e) { return []; }
  }
  function writeRecent(arr) {
    try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(arr)); } catch (e) { /* ignore */ }
  }

  if (!customElements.get('recently-viewed')) {
    customElements.define(
      'recently-viewed',
      class RecentlyViewed extends HTMLElement {
        connectedCallback() {
          var current = this.dataset.currentHandle || '';
          var limit = Number(this.dataset.limit) || 4;
          var list = readRecent();
          if (current) {
            writeRecent([current].concat(list.filter(function (h) { return h !== current; })).slice(0, 12));
          }
          var handles = list.filter(function (h) { return h !== current; }).slice(0, limit);
          if (!handles.length) return;
          var self = this;
          var grid = this.querySelector('[data-recent-grid]');
          if (!grid) return;
          Promise.all(
            handles.map(function (h) {
              return fetch('/products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' } })
                .then(function (r) { return r.ok ? r.json() : null; })
                .catch(function () { return null; });
            })
          ).then(function (products) {
            var shown = 0;
            products.forEach(function (p) {
              if (!p) return;
              grid.appendChild(self.card(p));
              shown += 1;
            });
            if (shown) self.removeAttribute('hidden');
          });
        }

        card(p) {
          var li = document.createElement('li');
          li.className = 'ml-recent__item';
          var a = document.createElement('a');
          a.className = 'ml-recent__link';
          a.href = p.url;
          if (p.featured_image) {
            var img = document.createElement('img');
            img.className = 'ml-recent__image';
            img.loading = 'lazy';
            img.alt = '';
            img.width = 400;
            img.height = 500;
            img.src = p.featured_image + (p.featured_image.indexOf('?') === -1 ? '?' : '&') + 'width=400';
            a.appendChild(img);
          }
          var title = document.createElement('span');
          title.className = 'ml-recent__title';
          title.textContent = p.title;
          a.appendChild(title);
          var price = document.createElement('span');
          price.className = 'ml-recent__price';
          price.textContent = money(p.price);
          if (p.compare_at_price > p.price) {
            var s = document.createElement('s');
            s.className = 'ml-recent__was';
            s.textContent = money(p.compare_at_price);
            price.appendChild(document.createTextNode(' '));
            price.appendChild(s);
          }
          a.appendChild(price);
          li.appendChild(a);
          return li;
        }
      }
    );
  }
})();
